import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { contactInformationSchema } from '../schema';
import { toast } from 'react-toastify';
import { useEditProfileMutation } from '@common/cache';
import { paths } from '@common/types';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { InferType } from 'yup';

type IContactInformationFormShape = Partial<
  InferType<typeof contactInformationSchema>
>;

const initialValues: Partial<IContactInformationFormShape> = {
  city: '',
  suite: '',
  state: '',
  county: '',
  street: '',
  zip_code: '',
  phone_number: '',
  mobile_phone_number: '',
  google_places_id: null,
};

interface IUseContactInformationProps {
  profile: paths[`/profile/{user_id}`]['get']['responses']['200']['content']['application/json'] & {
    id: string;
  };
}

export const useContactInformation = ({
  profile,
}: IUseContactInformationProps) => {
  const editProfileMutation = useEditProfileMutation();

  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(contactInformationSchema),
    defaultValues: {
      ...initialValues,
      ...profile,
      ...profile.address,
    },
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting, isDirty, errors },
  } = formMethods;

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: IContactInformationFormShape) => {
    try {
      await editProfileMutation.mutateAsync({
        ...values,
        country: 'USA',
      });

      toast.success('You successfully updated profile.');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  return {
    formMethods,
    onSubmit,
    isSubmitEnabled:
      isDirty && Object.keys(errors).length === 0 && !isSubmitting,
    handleSelectLocation,
  };
};
